import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../../dashboard.style.js';
import { Firestore } from 'firebase/firestore';
import { getAssetsByCompany, getWorkOrdersByCompany, getDowntimesByCompany, getPreventiveMaintenanceByCompany } from '@cmms/api';
import { WorkOrderStatusCat } from '@cmms/models';

@customElement('assets-mini-dashboard')
export class AssetsMiniDashboard extends LitElement {
  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  @state() private totalAssets = 0;
  @state() private idleAssets = 0;
  @state() private downAssets = 0;
  @state() private recentServiced: Array<{ asset: string; lastDate: number; status: number }> = [];
  @state() private assetsWithOpenWOs = 0;
  @state() private assetsWithOverduePMs = 0;
  @state() private avgCompletionHrs = 0;
  @state() private topByWOCount: Array<{ asset: string; count: number }> = [];
  @state() private topByPMCount: Array<{ asset: string; count: number }> = [];
  @state() private topByDowntime: Array<{ asset: string; hours: number }> = [];

  static styles = [
    styles,
    css`
      .mini-dashboard { display:flex; flex-direction:column; gap:1.5rem; background: var(--sl-color-neutral-50); padding:1rem; }
      .mini-header { display:flex; align-items:center; gap:1rem; }
      .donut-chart { width:140px; height:140px; border-radius:50%; box-shadow: inset 0 0 0 18px #fff; }
      .content { display:flex; gap:1.5rem; align-items:center; }
      .chart-legend { display:flex; flex-direction:column; gap:0.5rem; }
      .legend-item { display:flex; align-items:center; gap:0.5rem; }
      .legend-color { width:12px; height:12px; border-radius:50%; }
      .data-table { width:100%; border-collapse:collapse; }
      .data-table th, .data-table td { padding:0.75rem; text-align:left; border-bottom:1px solid var(--sl-color-neutral-200); }
      .data-table th { color: var(--sl-color-neutral-600); font-weight:600; font-size:0.875rem; }
      .data-table td { color: var(--sl-color-neutral-900); font-size:0.875rem; }
      .analytics-grid { display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 1.5rem; }
      @media (max-width: 900px) { .analytics-grid { grid-template-columns: 1fr; } }
      .metric-card { background:white; border-radius:1rem; box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.06); padding:1rem; display:flex; flex-direction:row; align-items:center; gap:0.75rem; }
      .metric-icon { width: 28px; height: 28px; border-radius: 999px; display:flex; align-items:center; justify-content:center; }
      .metric-content { display:flex; flex-direction:column; }
      .metric-title { color: var(--sl-color-neutral-600); font-size:0.85rem; }
      .metric-value { color: var(--sl-color-neutral-900); font-weight:600; font-size:1.25rem; }
    `
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId')) this.loadData();
  }

  private formatDate(ts?: number) { return ts ? new Date(ts).toLocaleDateString() : ''; }

  private async loadData() {
    if (!this.companyId || !this.firestore) return;
    try {
      const [assetsResp, woResp, pmResp, dtResp] = await Promise.all([
        getAssetsByCompany(this.firestore, this.companyId),
        getWorkOrdersByCompany(this.firestore, this.companyId),
        getPreventiveMaintenanceByCompany(this.firestore, this.companyId),
        getDowntimesByCompany(this.firestore, this.companyId)
      ]);
      const assets: any[] = Array.isArray((assetsResp as any).data) ? (assetsResp as any).data : (Array.isArray(assetsResp) ? (assetsResp as any) : []);
      const wos: any[] = Array.isArray((woResp as any).data) ? (woResp as any).data : (Array.isArray(woResp) ? (woResp as any) : []);
      const pms: any[] = Array.isArray((pmResp as any).data) ? (pmResp as any).data : (Array.isArray(pmResp) ? (pmResp as any) : []);
      const dts: any[] = Array.isArray((dtResp as any).data) ? (dtResp as any).data : (Array.isArray(dtResp) ? (dtResp as any) : []);

      const assetStatus: Record<string, string> = {};
      for (const a of assets) {
        const m = a.model || {};
        assetStatus[a.id] = (m.status || a.status || 'Active') + '';
      }

      this.totalAssets = assets.length;
      this.idleAssets = Object.values(assetStatus).filter(s => s.toLowerCase() === 'idle').length;

      // Down assets = assets with active downtime
      const activeDown = new Set<string>();
      for (const dt of dts) {
        const m = dt.model || {};
        if (m.ended_at === null && m.asset_id) activeDown.add(m.asset_id);
      }
      this.downAssets = activeDown.size;

      // Recently serviced assets: last completed WO per asset
      const lastByAsset: Record<string, { date: number; status: number }> = {};
      for (const wo of wos) {
        const m = wo.model || {};
        const assetId = m.asset_id || wo.asset_id;
        if (!assetId) continue;
        const status = (m.status_cat ?? wo.status_cat) as number;
        const ts = (m.updated_at ?? m.created_at ?? wo.updated_at ?? wo.created_at) as number | undefined;
        if (!ts) continue;
        if (!lastByAsset[assetId] || ts > lastByAsset[assetId].date) {
          lastByAsset[assetId] = { date: ts, status };
        }
      }
      // Asset names
      const assetName: Record<string, string> = {};
      for (const a of assets) {
        const m = a.model || {};
        assetName[a.id] = m.name || a.name || 'Asset';
      }
      this.recentServiced = Object.entries(lastByAsset)
        .map(([assetId, info]) => ({ asset: assetName[assetId] || assetId, lastDate: info.date, status: info.status }))
        .sort((a, b) => b.lastDate - a.lastDate)
        .slice(0, 5);

      // Analytics computations
      const woByAsset: Record<string, any[]> = {};
      const pmByAsset: Record<string, any[]> = {};
      const dtByAsset: Record<string, any[]> = {};

      for (const wo of wos) {
        const m = wo.model || {};
        const aid = m.asset_id || wo.asset_id;
        if (!aid) continue;
        (woByAsset[aid] ||= []).push(wo);
      }
      for (const pm of pms) {
        const m = pm.model || {};
        const aid = m.asset_id || pm.asset_id;
        if (!aid) continue;
        (pmByAsset[aid] ||= []).push(pm);
      }
      for (const dt of dts) {
        const m = dt.model || {};
        const aid = m.asset_id || dt.asset_id;
        if (!aid) continue;
        (dtByAsset[aid] ||= []).push(dt);
      }

      // Assets with open WOs
      const isOpen = (s: number) => s === WorkOrderStatusCat.OPEN || s === WorkOrderStatusCat.IN_PROGRESS;
      this.assetsWithOpenWOs = Object.keys(woByAsset).filter(aid => (woByAsset[aid] || []).some(wo => isOpen((wo.model?.status_cat ?? wo.status_cat) as number))).length;

      // Assets with overdue PMs
      const now = Date.now();
      this.assetsWithOverduePMs = Object.keys(pmByAsset).filter(aid => (pmByAsset[aid] || []).some(pm => {
        const m = pm.model || {};
        const due = m.next_due_at ?? m.pm_due_at;
        const grace = Number(m.grace_days || 0) * 86400000;
        const active = m.active !== false;
        return active && typeof due === 'number' && (due + grace) < now;
      })).length;

      // Average WO completion time per asset (overall)
      const completed = wos.filter(wo => !!(wo.model?.completed_at ?? wo.completed_at) && !!(wo.model?.created_at ?? wo.created_at));
      const avgHrs = completed.map(wo => {
        const c = (wo.model?.created_at ?? wo.created_at) as number;
        const f = (wo.model?.completed_at ?? wo.completed_at) as number;
        return Math.max(0, f - c) / 3.6e6;
      });
      this.avgCompletionHrs = avgHrs.length ? +(avgHrs.reduce((a,b)=>a+b,0) / avgHrs.length).toFixed(1) : 0;

      // Downtime hours per asset
      const dtHours: Record<string, number> = {};
      for (const [aid, list] of Object.entries(dtByAsset)) {
        const hrs = (list as any[]).reduce((sum, dt) => {
          const m = dt.model || {};
          const start = m.started_at ?? dt.started_at;
          const end = m.ended_at ?? dt.ended_at ?? null;
          if (typeof start === 'number' && typeof end === 'number') {
            return sum + Math.max(0, end - start) / 3.6e6;
          }
          return sum;
        }, 0);
        dtHours[aid] = +(hrs.toFixed(1));
      }

      // Top lists
      this.topByWOCount = Object.entries(woByAsset)
        .map(([aid, list]) => ({ asset: assetName[aid] || aid, count: list.length }))
        .sort((a, b) => b.count - a.count).slice(0, 5);
      this.topByPMCount = Object.entries(pmByAsset)
        .map(([aid, list]) => ({ asset: assetName[aid] || aid, count: list.length }))
        .sort((a, b) => b.count - a.count).slice(0, 5);
      this.topByDowntime = Object.entries(dtHours)
        .map(([aid, hours]) => ({ asset: assetName[aid] || aid, hours }))
        .sort((a, b) => b.hours - a.hours).slice(0, 5);

      this.requestUpdate();
    } catch (err) {
      console.error('Failed to load assets mini data:', err);
      this.totalAssets = 0;
      this.idleAssets = 0;
      this.downAssets = 0;
      this.recentServiced = [];
      this.assetsWithOpenWOs = 0;
      this.assetsWithOverduePMs = 0;
      this.avgCompletionHrs = 0;
      this.topByWOCount = [];
      this.topByPMCount = [];
      this.topByDowntime = [];
    }
  }

  private loadAnalytics = () => {
    this.loadData();
  };

  private statusBadgeVariant(status: number) {
    switch (status) {
      case WorkOrderStatusCat.COMPLETED: return 'success';
      case WorkOrderStatusCat.IN_PROGRESS: return 'warning';
      default: return 'primary';
    }
  }

  render() {
    return html`
      <div class="mini-dashboard">
        <div class="mini-header" style="display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem; justify-content:space-between;">
          <h2 style="margin:0;font-size:1.25rem; flex:1;">Assets Overview</h2>
          <sl-button size="small" variant="neutral" @click=${this.loadAnalytics}>
            <sl-icon name="arrow-clockwise" slot="prefix"></sl-icon>
            Refresh
          </sl-button>
        </div>

        <hr class="my-4 border-gray-200" style="border: 1px solid var(--sl-color-neutral-200); border-left: 0; border-right: 0; border-bottom: 0;" />

        <!-- KPI Cards (responsive grid) -->
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <sl-card class="flex items-center justify-between p-4">
            <div class="flex items-center space-x-3">
              <sl-icon name="wrench" style="color: orange; font-size: 24px;"></sl-icon>
              <div>
                <div class="text-sm text-gray-500">Assets with Open WOs</div>
                <div class="text-2xl font-semibold">${this.assetsWithOpenWOs ?? '-'}</div>
              </div>
            </div>
          </sl-card>

          <sl-card class="flex items-center justify-between p-4">
            <div class="flex items-center space-x-3">
              <sl-icon name="alert-triangle" style="color: red; font-size: 24px;"></sl-icon>
              <div>
                <div class="text-sm text-gray-500">Assets with Overdue PMs</div>
                <div class="text-2xl font-semibold">${this.assetsWithOverduePMs ?? '-'}</div>
              </div>
            </div>
          </sl-card>

          <sl-card class="flex items-center justify-between p-4">
            <div class="flex items-center space-x-3">
              <sl-icon name="clock" style="color: green; font-size: 24px;"></sl-icon>
              <div>
                <div class="text-sm text-gray-500">Avg WO Completion Time</div>
                <div class="text-2xl font-semibold">${typeof this.avgCompletionHrs === 'number' ? this.avgCompletionHrs.toFixed(1) : '-'} h</div>
              </div>
            </div>
          </sl-card>

          <sl-card class="flex items-center justify-between p-4">
            <div class="flex items-center space-x-3">
              <sl-icon name="database" style="color: dodgerblue; font-size: 24px;"></sl-icon>
              <div>
                <div class="text-sm text-gray-500">Total Assets</div>
                <div class="text-2xl font-semibold">${this.totalAssets ?? '-'}</div>
              </div>
            </div>
          </sl-card>
        </div>

        <div class="widget">
          <h3>Availability</h3>
          <div class="content">
            <div class="donut-chart" style="background: conic-gradient(
              var(--sl-color-success-500) 0deg ${Math.round((Math.max(0, this.totalAssets - this.downAssets - this.idleAssets) / Math.max(1, this.totalAssets)) * 360)}deg,
              var(--sl-color-danger-500) ${Math.round((Math.max(0, this.totalAssets - this.downAssets - this.idleAssets) / Math.max(1, this.totalAssets)) * 360)}deg ${Math.round(((Math.max(0, this.totalAssets - this.downAssets - this.idleAssets) + this.downAssets) / Math.max(1, this.totalAssets)) * 360)}deg,
              var(--sl-color-neutral-400) ${Math.round(((Math.max(0, this.totalAssets - this.downAssets - this.idleAssets) + this.downAssets) / Math.max(1, this.totalAssets)) * 360)}deg 360deg
            );"></div>
            <div class="chart-legend">
              <div class="legend-item"><span class="legend-color" style="background: var(--sl-color-success-500)"></span> Active ${Math.max(0, this.totalAssets - this.downAssets - this.idleAssets)}</div>
              <div class="legend-item"><span class="legend-color" style="background: var(--sl-color-danger-500)"></span> Down ${this.downAssets}</div>
              <div class="legend-item"><span class="legend-color" style="background: var(--sl-color-neutral-400)"></span> Idle ${this.idleAssets}</div>
            </div>
          </div>
        </div>

        

        <sl-card class="widget">
          <h3>Recently Serviced Assets</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Asset</th><th>Last WO Date</th><th>Status</th></tr></thead>
              <tbody>
                ${this.recentServiced.map(r => html`
                  <tr>
                    <td>${r.asset}</td>
                    <td>${this.formatDate(r.lastDate)}</td>
                    <td><sl-badge variant=${this.statusBadgeVariant(r.status)}>
                      ${r.status === WorkOrderStatusCat.COMPLETED ? 'Completed' : r.status === WorkOrderStatusCat.IN_PROGRESS ? 'In Progress' : 'Open'}
                    </sl-badge></td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </sl-card>

        <sl-card class="widget">
          <h3>Top 5 Assets by Work Orders</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Asset</th><th>WO Count</th></tr></thead>
              <tbody>
                ${this.topByWOCount.map(r => html`<tr><td>${r.asset}</td><td>${r.count}</td></tr>`)}
              </tbody>
            </table>
          </div>
        </sl-card>

        <sl-card class="widget">
          <h3>Top 5 Assets by PMs</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Asset</th><th>PM Count</th></tr></thead>
              <tbody>
                ${this.topByPMCount.map(r => html`<tr><td>${r.asset}</td><td>${r.count}</td></tr>`)}
              </tbody>
            </table>
          </div>
        </sl-card>

        <sl-card class="widget">
          <h3>Top 5 Assets by Downtime</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Asset</th><th>Downtime (hrs)</th></tr></thead>
              <tbody>
                ${this.topByDowntime.map(r => html`<tr><td>${r.asset}</td><td>${r.hours}</td></tr>`)}
              </tbody>
            </table>
          </div>
        </sl-card>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'assets-mini-dashboard': AssetsMiniDashboard;
  }
}
